import React, { useState, useEffect } from 'react';
import { ref, onValue, remove } from 'firebase/database';
import { database } from '../firebase';
import ContentSender from '../components/ContentSender';

const DELETE_API_URL = 'https://nutrisync.dgapp.com.br/api/delete-content.php';

export default function ContentManagement() {
    const [contentList, setContentList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSender, setShowSender] = useState(false);
    const [editingContent, setEditingContent] = useState(null);

    useEffect(() => {
        const contentRef = ref(database, 'contentForNutritionists');
        const unsubscribe = onValue(contentRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const contentArray = Object.entries(data)
                    .map(([id, content]) => ({
                        id,
                        ...content
                    }))
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setContentList(contentArray);
            } else {
                setContentList([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const getTypeLabel = (type) => {
        switch (type) {
            case 'video':
                return '🎥 Vídeo';
            case 'card':
                return '🃏 Card';
            case 'article':
                return '📄 Artigo';
            default:
                return type;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Data não disponível';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleEdit = (content) => {
        setEditingContent(content);
        setShowSender(true);
    };

    const handleDelete = async (contentId, contentTitle) => {
        if (!window.confirm(`Tem certeza que deseja excluir o conteúdo "${contentTitle}"? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            // 1. Excluir do MySQL (usando título para garantir sincronia)
            console.log('Excluindo do MySQL:', contentTitle);
            await fetch(DELETE_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: contentTitle })
            });

            // 2. Excluir do Firebase
            console.log('Excluindo do Firebase:', contentId);
            const contentRef = ref(database, `contentForNutritionists/${contentId}`);
            await remove(contentRef);
            
            alert('Conteúdo excluído com sucesso!');
        } catch (error) {
            console.error('Erro ao excluir conteúdo:', error);
            alert('Erro ao excluir conteúdo. Tente novamente.');
        }
    };

    const handleCloseSender = () => {
        setShowSender(false);
        setEditingContent(null);
    };

    const handleNewContent = () => {
        setEditingContent(null);
        setShowSender(true);
    };

    if (loading) {
        return <div className="container" style={{ textAlign: 'center', marginTop: '5rem' }}>Carregando...</div>;
    }

    return (
        <div className="container">
            <header className="dashboard-header">
                <div>
                    <h1 style={{ fontSize: '2rem', fontWeight: 700 }}>Gerenciamento de Conteúdo</h1>
                    <p style={{ color: 'var(--text-muted)' }}>Envie vídeos, cards e artigos científicos para nutricionistas</p>
                </div>
                <button
                    onClick={handleNewContent}
                    className="btn btn-primary"
                >
                    + Novo Conteúdo
                </button>
            </header>

            {showSender && (
                <ContentSender 
                    onClose={handleCloseSender}
                    editingContent={editingContent}
                />
            )}

            <section className="table-container" style={{ marginTop: '2rem' }}>
                <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Histórico de Conteúdo Enviado</h2>
                {contentList.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '3rem',
                        color: 'var(--text-muted)',
                        background: 'var(--bg-card)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                        <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Nenhum conteúdo enviado ainda</p>
                        <p>Clique em "Novo Conteúdo" para começar a enviar conteúdo para os nutricionistas</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        {contentList.map((content) => (
                            <div
                                key={content.id}
                                style={{
                                    background: 'var(--bg-card)',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    transition: 'transform 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                background: 'rgba(0, 208, 156, 0.1)',
                                                color: 'var(--primary)',
                                                borderRadius: '20px',
                                                fontSize: '0.85rem',
                                                fontWeight: 600
                                            }}>
                                                {getTypeLabel(content.type)}
                                            </span>
                                            <h3 style={{ margin: 0, color: 'var(--text-main)' }}>{content.title}</h3>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                            <button
                                                onClick={() => handleEdit(content)}
                                                className="btn"
                                                style={{
                                                    padding: '0.4rem 0.8rem',
                                                    fontSize: '0.85rem',
                                                    background: 'rgba(0, 208, 156, 0.1)',
                                                    color: 'var(--primary)',
                                                    border: '1px solid rgba(0, 208, 156, 0.3)',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = 'rgba(0, 208, 156, 0.2)';
                                                    e.target.style.borderColor = 'var(--primary)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = 'rgba(0, 208, 156, 0.1)';
                                                    e.target.style.borderColor = 'rgba(0, 208, 156, 0.3)';
                                                }}
                                            >
                                                ✏️ Editar
                                            </button>
                                            <button
                                                onClick={() => handleDelete(content.id, content.title)}
                                                className="btn"
                                                style={{
                                                    padding: '0.4rem 0.8rem',
                                                    fontSize: '0.85rem',
                                                    background: 'rgba(239, 68, 68, 0.1)',
                                                    color: '#ef4444',
                                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                                    e.target.style.borderColor = '#ef4444';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                                                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                                                }}
                                            >
                                                🗑️ Excluir
                                            </button>
                                        </div>
                                        {content.content && content.type === 'article' && (
                                            <p style={{
                                                color: 'var(--text-muted)',
                                                marginBottom: '0.5rem',
                                                lineHeight: 1.6
                                            }}>
                                                {content.content.substring(0, 200)}...
                                            </p>
                                        )}
                                        {content.url && content.type === 'video' && (
                                            <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                                <a href={content.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                                                    {content.url}
                                                </a>
                                            </p>
                                        )}
                                        {content.pdfUrl && (
                                            <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                                <a href={content.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                                                    📄 {content.pdfFileName ? `Ver ${content.pdfFileName}` : 'Ver PDF'}
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    paddingTop: '1rem',
                                    borderTop: '1px solid rgba(255,255,255,0.05)',
                                    fontSize: '0.85rem',
                                    color: 'var(--text-muted)'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        <span>Enviado para {Array.isArray(content.sentTo) ? content.sentTo.length : (content.sentTo ? Object.keys(content.sentTo).length : 0)} nutricionista(s)</span>
                                        {content.scheduledDate && (
                                            <span style={{ 
                                                color: new Date(content.scheduledDate) > new Date() 
                                                    ? 'var(--primary)' 
                                                    : 'var(--text-muted)',
                                                fontSize: '0.8rem'
                                            }}>
                                                📅 Agendado para: {formatDate(content.scheduledDate)}
                                                {new Date(content.scheduledDate) > new Date() && ' (Futuro)'}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <span>Criado em: {formatDate(content.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}




